import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Wordmark } from "./Logo";
import { useAuth } from "../context/AuthContext";
import { AuthModal } from "./AuthModal";
import { Moon, Sun, Menu, X, ShieldCheck } from "lucide-react";

export function Navbar({ onRequireAuth }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [authOpen, setAuthOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dark, setDark] = useState(() => (localStorage.getItem("opn_theme") || "dark") === "dark");

  const toggleTheme = () => {
    const next = !dark;
    setDark(next);
    localStorage.setItem("opn_theme", next ? "dark" : "light");
    document.documentElement.classList.toggle("dark", next);
  };

  const links = [
    { to: "/search", label: "Find Wi-Fi" },
    { to: "/plans", label: "Pricing" },
  ];

  const handleLogout = async () => { await logout(); navigate("/"); };

  const openAuth = () => (onRequireAuth ? onRequireAuth() : setAuthOpen(true));

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/80 backdrop-blur-xl">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo"><Wordmark /></Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/\s/g,'-')}`}
              className={`text-sm transition-colors hover:text-foreground ${location.pathname === l.to ? "text-foreground" : "text-muted-foreground"}`}>
              {l.label}
            </Link>
          ))}
          {user && user.role === "host" && <Link to="/dashboard" data-testid="nav-dashboard" className="text-sm text-muted-foreground hover:text-foreground">Host Dashboard</Link>}
          {user && user.role === "admin" && <Link to="/admin" data-testid="nav-admin" className="text-sm text-muted-foreground hover:text-foreground">Admin</Link>}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <button onClick={toggleTheme} data-testid="theme-toggle" className="h-9 w-9 grid place-items-center rounded-full border border-border hover:bg-accent transition-colors">
            {dark ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          {user ? (
            <div className="flex items-center gap-3">
              <Link to="/verify" data-testid="nav-verify" className="flex items-center gap-1.5 text-xs font-mono px-3 py-1.5 rounded-full border border-border">
                <ShieldCheck size={13} className={user.verification_status === "verified" ? "text-emerald-500" : "text-muted-foreground"} />
                {user.verification_status === "verified" ? "VERIFIED" : "VERIFY ID"}
              </Link>
              <button onClick={handleLogout} data-testid="logout-btn" className="text-sm px-4 py-2 rounded-full bg-foreground text-background hover:opacity-90 transition-opacity">
                Sign out
              </button>
            </div>
          ) : (
            <button onClick={openAuth} data-testid="nav-signin-btn" className="text-sm px-5 py-2 rounded-full bg-foreground text-background hover:opacity-90 transition-opacity">
              Sign in
            </button>
          )}
        </div>

        <button className="md:hidden h-9 w-9 grid place-items-center" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-btn">
          {mobileOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-border px-5 py-4 flex flex-col gap-3 bg-background">
          {links.map((l) => <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)} className="text-sm text-muted-foreground py-1">{l.label}</Link>)}
          {user && user.role === "host" && <Link to="/dashboard" onClick={() => setMobileOpen(false)} className="text-sm py-1">Host Dashboard</Link>}
          {user && user.role === "admin" && <Link to="/admin" onClick={() => setMobileOpen(false)} className="text-sm py-1">Admin</Link>}
          {user && <Link to="/verify" onClick={() => setMobileOpen(false)} className="text-sm py-1">Verify ID</Link>}
          <button onClick={toggleTheme} className="text-sm text-left py-1 text-muted-foreground">Toggle theme</button>
          {user ? (
            <button onClick={handleLogout} className="text-sm px-4 py-2 rounded-full bg-foreground text-background w-full">Sign out</button>
          ) : (
            <button onClick={() => { setMobileOpen(false); openAuth(); }} className="text-sm px-4 py-2 rounded-full bg-foreground text-background w-full">Sign in</button>
          )}
        </div>
      )}

      {!onRequireAuth && <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />}
    </header>
  );
}
